import { ServerConfig } from "@/config/config";
import { Bank } from "@/models/base";
import { CodaBoxDocument } from "@/models/codabox-document";
import { CodaBoxConfig } from "@/models/dossier-configs/codabox-config";
import { Axios } from "@/utils/axios";
import Vue from "vue";
import { ICodaImportResult } from "./coda-service";

export interface ICodaBoxStatus {
	Connected: boolean;
	PendingCount: number;
}

export interface ICodaBoxSyncResult {
	Documents: CodaBoxDocument[];
	Count: number;
	SkippedCount: number;
}

export const CodaBoxService = new Vue({
	data() {
		return {};
	},
	computed: {
		url(): string {
			return `${ServerConfig.host}/codabox`;
		}
	},
	methods: {
		async requestAccessToken(): Promise<{ vat: string; email: string }> {
			let response = await Axios.post(`${this.url}/request-access-token`);
			return response.data;
		},
		async exchangeToken(token: string, startDate: string): Promise<{ Connected: boolean }> {
			let response = await Axios.post(`${this.url}/exchange-token`, {
				Token: token,
				StartDate: startDate,
			});
			return response.data;
		},
		async disconnect(): Promise<{ Connected: boolean }> {
			let response = await Axios.post(`${this.url}/disconnect`);
			return response.data;
		},
		async updateSettings(config: CodaBoxConfig): Promise<void> {
			await Axios.put(`${this.url}/settings`, {
				FetchCoda: config.FetchCoda,
				FetchSoda: config.FetchSoda,
				FetchPurchaseInvoices: config.FetchPurchaseInvoices,
				FetchSalesInvoices: config.FetchSalesInvoices,
			});
		},
		async getStatus(): Promise<ICodaBoxStatus> {
			let response = await Axios.get(`${this.url}/status`);
			return response.data as ICodaBoxStatus;
		},
		async sync(): Promise<ICodaBoxSyncResult> {
			let response = await Axios.post(`${this.url}/sync`);
			let result = response.data as ICodaBoxSyncResult;
			result.Documents = (result.Documents || []).map((d: any) => new CodaBoxDocument(d));
			return result;
		},
		async getDocuments(status?: string, type?: string): Promise<CodaBoxDocument[]> {
			let params = new URLSearchParams();
			if (status) params.set("status", status);
			if (type) params.set("type", type);
			let response = await Axios.get(`${this.url}/documents?${params.toString()}`);
			return (response.data as any[]).map((d: any) => new CodaBoxDocument(d));
		},
		async getPreview(docId: number, bankId: number): Promise<ICodaImportResult> {
			let response = await Axios.get(`${this.url}/preview/${docId}?bank-id=${bankId}`);
			let result = response.data as ICodaImportResult;
			result.Banks = result.Banks.map((b: any) => new Bank(b));
			return result;
		},
		async confirmDocument(docId: number, banks: Bank[]): Promise<Bank[]> {
			let response = await Axios.post(`${this.url}/confirm/${docId}`, banks);
			return (response.data as any[]).map((b: any) => new Bank(b));
		},
		async skipDocument(docId: number): Promise<CodaBoxDocument> {
			let response = await Axios.post(`${this.url}/skip/${docId}`);
			return new CodaBoxDocument(response.data);
		},
		async resetDocument(docId: number): Promise<CodaBoxDocument> {
			let response = await Axios.post(`${this.url}/reset/${docId}`);
			return new CodaBoxDocument(response.data);
		},
		getDownloadUrl(docId: number): string {
			return `${this.url}/download/${docId}`;
		},
	},
});
